import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { NbxCapacitorFirebaseAuth } from '@nbx/capacitor-firebase-auth';

import { Capacitor } from '@capacitor/core';

import isMobileDevice from '../../helpers/isMobileDevice';
import importFirebase from '../firebase-async';

import { getNativeUser, requireUser } from './helpers';

import type { FirebaseContext } from './context';
import type { FirebaseCoreMethods } from './types';
import type { FirebaseOptions } from 'firebase/app';

export function createFirebaseCore(context: FirebaseContext): FirebaseCoreMethods {
  const { log, isAndroid, isIOS, vippsScopes, state } = context;

  const initializeApp: FirebaseCoreMethods['initializeApp'] = async config => {
    if (state.importedFirebaseAuth) {
      return state.importedFirebaseAuth;
    }

    if (!state.initializePromise) {
      state.initializePromise = (async () => {
        const [firebaseApp, firebaseAuth] = await importFirebase();

        state.appInstance = firebaseApp.initializeApp(config);

        // See https://github.com/firebase/firebase-js-sdk/issues/5019
        if (window.PLATFORM !== 'web') {
          firebaseAuth.initializeAuth(state.appInstance, {
            persistence: firebaseAuth.indexedDBLocalPersistence
          });
        }

        state.vippsOpenIDConnectProvider = new firebaseAuth.OAuthProvider('oidc.vipps');
        state.vippsOpenIDConnectProvider.addScope(vippsScopes);

        state.googleOpenIDConnectProvider = new firebaseAuth.OAuthProvider('google.com');
        state.appleOpenIDConnectProvider = new firebaseAuth.OAuthProvider('apple.com');
        state.importedFirebaseAuth = firebaseAuth;

        return firebaseAuth;
      })().catch(error => {
        state.initializePromise = null;
        throw error;
      });
    }

    return state.initializePromise;
  };

  const importAuth: FirebaseCoreMethods['importAuth'] = () => {
    if (state.importedFirebaseAuth) {
      return Promise.resolve(state.importedFirebaseAuth);
    }

    return initializeApp(state.firebaseConfig);
  };

  const auth: FirebaseCoreMethods['auth'] = async () => {
    const firebaseAuth = await importAuth();
    return firebaseAuth.getAuth(state.appInstance ?? undefined);
  };

  const user: FirebaseCoreMethods['user'] = async () => {
    if (Capacitor.isNativePlatform()) {
      const nativeUser = await getNativeUser();
      if (nativeUser) {
        log.info('Using native user()');
        return nativeUser;
      }
    }

    return (await auth()).currentUser;
  };

  const signInWithCredential: FirebaseCoreMethods['signInWithCredential'] = async (
    firebaseCredential,
    reauthenticate
  ) => {
    if (!firebaseCredential) {
      throw new Error('signInWithCredential: no firebaseCredential provided');
    }

    log.info('signInWithCredential', !!firebaseCredential);
    const firebaseAuth = await importAuth();

    if (Capacitor.isNativePlatform()) {
      log.info('NbxCapacitorFirebaseAuth.signInWithCredential, native user');
      return reauthenticate
        ? NbxCapacitorFirebaseAuth.reauthenticateWithCredential(firebaseCredential)
        : NbxCapacitorFirebaseAuth.signInWithCredential(firebaseCredential);
    }

    log.info('Firebase.signInWithCredential, web user');

    try {
      const userCredential = await (reauthenticate
        ? firebaseAuth.reauthenticateWithCredential(
            await requireUser(user, 'reauthenticate with credential'),
            firebaseCredential
          )
        : firebaseAuth.signInWithCredential(await firebaseAuth.getAuth(), firebaseCredential));

      log.info('signInWithCredential userCredential=', !!userCredential);
      return userCredential;
    } catch (signInErrorOrMfaRequired: unknown) {
      console.error('signInWithCredential error:', signInErrorOrMfaRequired);
      (signInErrorOrMfaRequired as Record<string, unknown>).credential = firebaseCredential;
      throw signInErrorOrMfaRequired;
    }
  };

  const login: FirebaseCoreMethods['login'] = async (email, password) => {
    const firebaseAuth = await importAuth();
    const firebaseCredential = firebaseAuth.EmailAuthProvider.credential(email, password);
    return signInWithCredential(firebaseCredential, false);
  };

  const reauthenticateWithPassword: FirebaseCoreMethods['reauthenticateWithPassword'] = async (
    email,
    password
  ) => {
    const firebaseAuth = await importAuth();
    const firebaseCredential = firebaseAuth.EmailAuthProvider.credential(email, password);
    return signInWithCredential(firebaseCredential, true);
  };

  const reloadUser: FirebaseCoreMethods['reloadUser'] = async () => {
    if (Capacitor.isNativePlatform()) {
      const nativeUser = await getNativeUser();
      if (nativeUser) {
        log.info('Using native reload()');
        return FirebaseAuthentication.reload();
      }
    }

    const firebaseAuth = await importAuth();
    return firebaseAuth.reload(await requireUser(user, 'reload user'));
  };

  return {
    setConfig: (config: FirebaseOptions) => {
      if (!Capacitor.isNativePlatform() && (isAndroid || isIOS) && config.authDomain) {
        config.authDomain = config.authDomain.replace('app', 'auth');
      }
      state.firebaseConfig = config;
      return state.firebaseConfig;
    },

    initializeApp,

    importAuth,

    auth,

    isNativeAuth: Capacitor.isNativePlatform,

    user,

    getIdToken: async forceRefresh => {
      log.info('getIdToken, Firebase.isNativeAuth() = ', Capacitor.isNativePlatform());

      if (Capacitor.isNativePlatform()) {
        log.info('getIdTokenFromNative');
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          return FirebaseAuthentication.getIdToken({ forceRefresh }).then(res => res.token);
        }
      }

      log.info('getIdToken');
      const firebaseAuth = await importAuth();
      return firebaseAuth.getIdToken(await requireUser(user, 'getIdToken'), forceRefresh);
    },

    signUp: async (email, password) => {
      const firebaseAuth = await importAuth();
      const authInstance = await firebaseAuth.getAuth();
      return firebaseAuth.createUserWithEmailAndPassword(authInstance, email, password);
    },

    getAdditionalUserInfo: async userCredential => {
      if (Capacitor.isNativePlatform()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          return FirebaseAuthentication.getAdditionalUserInfo();
        }
      }

      const firebaseAuth = await importAuth();
      return firebaseAuth.getAdditionalUserInfo(
        userCredential as Parameters<typeof firebaseAuth.getAdditionalUserInfo>[0]
      );
    },

    onAuthStateChanged: async (nextOrObserver, error) => {
      const firebaseAuth = await importAuth();
      return firebaseAuth.onAuthStateChanged(await auth(), nextOrObserver, error);
    },

    signInWithCredential,

    login,

    reauthenticateWithPassword,

    getRedirectResult: async () => {
      const firebaseAuth = await importAuth();
      const authInstance = await firebaseAuth.getAuth();
      return firebaseAuth.getRedirectResult(authInstance);
    },

    isRedirectFlow: () => isMobileDevice(),

    logout: async () => {
      if (Capacitor.isNativePlatform()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native signout()');
          return FirebaseAuthentication.signOut();
        }
      }

      const firebaseAuth = await importAuth();
      const authInstance = await firebaseAuth.getAuth();
      return firebaseAuth.signOut(authInstance);
    },

    reloadUser,

    reauthenticate: (email, password) => {
      return login(email, password);
    }
  };
}
